import { useEffect, useRef } from 'react'
import { useShallow } from 'zustand/react/shallow'
import { createFileRoute, Outlet } from '@tanstack/react-router'
import AppSidebar from '@/components/app-sidebar'
import { SidebarProvider, SidebarTrigger } from '@/components/ui/sidebar'
import { Layout } from '@/components/custom/layout'
import { UserNav } from '@/components/user-nav'
import { TabsNav } from '@/components/tabs-nav'
import { Separator } from '@/components/ui/separator'
import { navigateGuard } from '@/utils/auth'
import PhoneMenuIcon from '@/components/phone/phone-menu-icon'
import { useQuery } from '@tanstack/react-query'
import { getChatList } from '@/api/chat.js'
import useChatStore from '@/store/chat-store.js'
import useTenantConfigStore from '@/store/tenant-config-store'
import {
  connectSocket as connectChat,
  disconnectSocket as disconnectChat,
} from '@/utils/chat_socket.js'
import { requestPermission } from '@/utils/notification.js'
import { ChannelStatus } from '@/components/channel-status.jsx'
import useOfflineNotification from '@/hooks/use-offline-notification'
import { useTabNavStore } from '@/store/tab-nav-store'
import { useNavigate, useLocation } from '@tanstack/react-router'
import { useState } from 'react'
import OrganizationCreateModal from '@/components/organization-detail-route/organization-create-modal'
import CustomerCreateModal from '@/components/customers-route/customer-create-modal'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Plus } from 'lucide-react'
import { Button } from '@/components/custom/button'
import { throwException } from '@/exceptions/throw-exception.js'
import { useWakeLock } from 'react-screen-wake-lock'
import {
  connectSocket as connectNotification,
  disconnectSocket as disconnectNotification,
} from '@/utils/notification_socket.js'
import { NotificationMenuIcon } from '@/components/notification/notification-menu-icon.jsx'
import QUERY_KEY from '@/constants/use-query-key.js'
import { useSettings } from '@/hooks/use-settings.js'
import { getTenantConfig } from '@/api/user'
import checkViewAsMode from '@/utils/check_view_as_mode_toast.js'

export const Route = createFileRoute('/_auth')({
  beforeLoad: ({ location }) => navigateGuard(location),
  component: Index,
})

const sideBarDefaultOpen = localStorage.getItem('sidebar-open')
const FIRST_LIMIT_CHAT = 500

function Index() {
  useSettings()
  const { request, release } = useWakeLock()
  const headerRef = useRef(null)
  const [tenantConfig, setTenantConfig] = useTenantConfigStore(
    useShallow((state) => [state.tenantConfig, state.setTenantConfig]),
  )
  const [isChatSocketConnected, setChatList, setIsChatListLoading] =
    useChatStore(
      useShallow((state) => [
        state.isChatSocketConnected,
        state.setChatList,
        state.setIsChatListLoading,
      ]),
    )
  requestPermission()
  useOfflineNotification()
  // initialize data chat;
  useQuery({
    queryKey: [QUERY_KEY.GET_CHAT_LIST],
    enabled: isChatSocketConnected,
    queryFn: async () => {
      try {
        const { data } = await getChatList(FIRST_LIMIT_CHAT)
        const chatList = data?.list || []
        setChatList(chatList)
        setIsChatListLoading(false)
        return data
      } catch (error) {
        setIsChatListLoading(false)
        throwException(error)
        return []
      }
    },
  })

  useQuery({
    enabled: !tenantConfig,
    queryKey: [QUERY_KEY.GET_TENANT_CONFIG],
    queryFn: async () => {
      const response = await getTenantConfig()
      setTenantConfig(response?.data)
    },
  })

  useEffect(() => {
    request('screen')
    return () => {
      release()
    }
  }, [request, release])

  useEffect(() => {
    connectChat()
    connectNotification()
    checkViewAsMode()
    return () => {
      disconnectChat()
      disconnectNotification()
    }
  }, [])

  const navigate = useNavigate()
  const { pathname, href } = useLocation()
  const tabs = useTabNavStore((s) => s.tabs)
  const setLastVisitedHref = useTabNavStore((s) => s.setLastVisitedHref)
  const noActiveTab = tabs.every((tab) => tab.pathname !== pathname)
  const [isOpenCustomerModal, setIsOpenCustomerModal] = useState(false)
  const [isOpenOrganizationModal, setIsOpenOrganizationModal] = useState(false)
  return (
    <SidebarProvider defaultOpen={sideBarDefaultOpen === 'true'}>
      <AppSidebar />
      <Layout>
        <Layout.Header ref={headerRef}>
          <SidebarTrigger className="-ml-1" />
          <Separator orientation="vertical" className="mr-2 h-4" />
          <div className="flex max-w-[calc(100%-314px)] flex-1">
            <TabsNav />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  className="ml-2"
                  leftSection={<Plus className="h-4 w-4 -translate-y-[1px]" />}
                >
                  Thêm mới
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem
                  onClick={() => {
                    if (noActiveTab) {
                      setLastVisitedHref(href)
                    }
                    const tempTicketId = `new-${Date.now()}`
                    navigate({
                      to: '/tickets/$ticketId',
                      params: { ticketId: tempTicketId },
                    })
                  }}
                >
                  Ticket
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsOpenCustomerModal(true)}>
                  Khách hàng
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={() => setIsOpenOrganizationModal(true)}
                >
                  Tổ chức
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <NotificationMenuIcon />
          <PhoneMenuIcon parentRef={headerRef} />
          <ChannelStatus />
          <OrganizationCreateModal
            setIsOpen={setIsOpenOrganizationModal}
            isOpen={isOpenOrganizationModal}
            customerId={null}
            handleCreateSuccess={() => setIsOpenOrganizationModal(false)}
          />

          <CustomerCreateModal
            isOpen={isOpenCustomerModal}
            handleCloseModal={() => setIsOpenCustomerModal(false)}
          />
          <UserNav />
        </Layout.Header>
        <Outlet />
      </Layout>
    </SidebarProvider>
  )
}
